// src/App.js
import React, { Suspense, useMemo, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Box, createTheme, ThemeProvider, useMediaQuery } from "@mui/material";

import AppBarComponent from "./components/layout/AppBar.jsx";
import SideBar from "./components/layout/SideBar.jsx";
import CustomTabs from "./components/layout/CustomTabs.jsx";
import AppFooter from "./components/layout/AppFooter.jsx";

import Home from "./pages/Home.jsx";
import Login from "./pages/Auth/Login.jsx";
import Register from "./pages/Auth/Register.jsx";

import CreateSupplier from "./pages/Supplier/CreateSupplier.jsx";
import ListSuppliers from "./pages/Supplier/ListSuppliers.jsx";
import EditSupplier from "./pages/Supplier/EditSupplier.jsx";

import CreateExecutive from "./pages/Executive/CreateExecutive.jsx";
import ListExecutive from "./pages/Executive/ListExecutive.jsx";
import EditExecutive from "./pages/Executive/EditExecutive.jsx";

import CreateProduct from "./pages/Product/CreateProduct.jsx";
import ListProduct from "./pages/Product/ListProduct.jsx";
import EditProduct from "./pages/Product/EditProduct.jsx";

import CreateOrder from "./pages/Order/CreateOrder.jsx";
import ListOrder from "./pages/Order/ListOrder.jsx";

import CreateInvoice from "./pages/Invoice/CreateInvoice.jsx";
import ConfirmInvoice from "./pages/Invoice/ConfirmInvoice.jsx";
import ListInvoice from "./pages/Invoice/ListInvoice.jsx";
import SellOutDocumentCreate from "./pages/SellOut/SellOutDocumentCreate.jsx";
import SellOutDocumentList from "./pages/SellOut/SellOutDocumentList.jsx";
import SellOutMissing from "./pages/SellOut/SellOutMissing.jsx";
import SellOutPromiseDetail from "./pages/SellOut/SellOutPromiseDetail.jsx";
import SellOutPromotions from "./pages/SellOut/SellOutPromotions.jsx";
import SellOutPromiseCreate from "./pages/SellOut/SellOutPromiseCreate.jsx";
import SellOutPromotionsLegacy from "./pages/SellOut/SellOutPromotionsLegacy.jsx";

import CreatePayment from "./pages/Payment/CreatePayment.jsx";
import PaymentsList from "./pages/Payment/PaymentsList.jsx";
import PaymentDetailView from "./pages/Payment/PaymentDetailView.jsx";
import ReceiptList from "./pages/Payment/ReceiptList.jsx";
import CreditNoteList from "./pages/Payment/CreditNoteList.jsx";
import AbastoObligations from "./pages/Abasto/AbastoObligations.jsx";
import DebtList from "./views/DebtList.jsx";
import KardexRecepciones from "./views/KardexRecepciones.jsx";
import KardexHomogeneo from "./views/KardexHomogeneo.jsx";
import InvoiceProviderStatusReport from "./views/InvoiceProviderStatusReport.jsx";

import UserProfile from "./pages/User/UserProfile.jsx";

import PermissionCatalogPage from "./pages/Admin/PermissionCatalogPage.jsx";
import RoleAdminPage from "./pages/Admin/RoleAdminPage.jsx";
import UserCompanyAdminPage from "./pages/Admin/UserCompanyAdminPage.jsx";
import CompanyAdminPage from "./pages/Admin/CompanyAdminPage.jsx";
import UsersAdminPage from "./pages/Admin/UserAdminPage.jsx";

import WarehouseList from "./pages/Catalogs/WarehouseList.jsx";
import FormatList from "./pages/Catalogs/FormatList.jsx";
import CategoryList from "./pages/Catalogs/CategoryList.jsx";
import PriceTypeList from "./pages/Catalogs/PriceTypeList.jsx";
import ObjectiveTypeList from "./pages/Catalogs/ObjectiveTypeList.jsx";
import InvoiceTypeList from "./pages/Catalogs/InvoiceTypeList.jsx";
import ReceiveNotesPage from "./pages/ReceiveNote/ReceiveNotesPage.jsx";

import NoAccessPage from "./pages/NoAccessPage.jsx";
import NotFoundPage from "./components/layout/NotFoundPage.jsx";

import { AuthProvider, useAuth } from "./auth/AuthProvider.jsx";
import ProtectedRoute from "./components/ProtectedRoute.jsx";

const DEFAULT_PRIMARY = "#10173c";
const DEFAULT_SECONDARY = "#6F130F";

const resolvePrimary = (value) => {
  if (typeof value !== "string") return DEFAULT_PRIMARY;
  const v = value.trim();
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)) return v;
  return DEFAULT_PRIMARY;
};

function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isMobile = useMediaQuery("(max-width: 768px)");
  const { isAuthenticated, authVersion } = useAuth();

  const toggleMobileDrawer = () => setMobileOpen(!mobileOpen);
  const contentMarginLeft = pinned && !isMobile ? "240px" : isMobile ? "0px" : "65px";

  // Si no está autenticado: solo login/register
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/register" element={<Register />} />
        {/* cualquier otra ruta te devuelve al login */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // App autenticada
  return (
    <div key={authVersion}>
      <AppBarComponent setDrawerOpen={setDrawerOpen} />
      <SideBar
        mobileOpen={mobileOpen}
        toggleMobileDrawer={toggleMobileDrawer}
        pinned={pinned}
        setPinned={setPinned}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
      />

      <Box
        component="main"
        sx={{
          marginLeft: contentMarginLeft,
          transition: "margin-left 0.4s ease-in-out",
          marginTop: "50px",
          width: `calc(100% - ${contentMarginLeft})`,
          maxWidth: "100vw",
          overflowX: "hidden",
          padding: "20px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(100vh - 50px)", // para que el footer quede abajo
        }}
      >
        {/* Tabs superiores */}
        <Box width="100%" sx={{ height: "40px", marginBottom: "8px" }}>
          <CustomTabs />
        </Box>

        {/* Contenido principal */}
        <Box sx={{ flexGrow: 1 }}>
          <Suspense fallback={<div />}>
            <Routes>
              {/* Home */}
              <Route path="/" element={<Home />} />
              <Route path="/home" element={<Home />} />

              {/* Suppliers */}
              <Route
                path="/suppliers/create-new"
                element={
                  <ProtectedRoute required={"manage_supplier"}>
                    <CreateSupplier />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/suppliers/all-suppliers"
                element={
                  <ProtectedRoute required={"view_supplier"}>
                    <ListSuppliers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/suppliers/edit-supplier/:id"
                element={
                  <ProtectedRoute required={"manage_supplier"}>
                    <EditSupplier />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/suppliers/executive/create-new"
                element={
                  <ProtectedRoute required={"manage_executive"}>
                    <CreateExecutive />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/suppliers/executive/all-executives"
                element={
                  <ProtectedRoute required={"view_executive"}>
                    <ListExecutive />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/suppliers/executive/edit-executives"
                element={
                  <ProtectedRoute required={"manage_executive"}>
                    <EditExecutive />
                  </ProtectedRoute>
                }
              />

              {/* Products */}
              <Route
                path="/orders/product/create-new"
                element={
                  <ProtectedRoute required={"manage_order"}>
                    <CreateProduct />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/orders/product/all-products"
                element={
                  <ProtectedRoute required={"view_order"}>
                    <ListProduct />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/orders/product/edit-products/:id"
                element={
                  <ProtectedRoute required={"manage_order"}>
                    <EditProduct />
                  </ProtectedRoute>
                }
              />

              {/* Orders */}
              <Route
                path="/orders/create-new"
                element={
                  <ProtectedRoute required={"create_order"}>
                    <CreateOrder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/orders/all-orders"
                element={
                  <ProtectedRoute required={"view_order"}>
                    <ListOrder />
                  </ProtectedRoute>
                }
              />

              {/* Invoice */}
              <Route
                path="/invoice/create-new"
                element={
                  <ProtectedRoute required={"create_invoice"}>
                    <CreateInvoice />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoice/confirm"
                element={
                  <ProtectedRoute required={"confirm_invoice"}>
                    <ConfirmInvoice />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoice/all-invoices"
                element={
                  <ProtectedRoute required={"view_invoice"}>
                    <ListInvoice />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sellout/promotions"
                element={
                  <ProtectedRoute required={["view_sellout", "manage_sellout"]}>
                    <SellOutPromotions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sellout/promotions/create"
                element={
                  <ProtectedRoute required={"manage_sellout"}>
                    <SellOutPromiseCreate />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sellout/promotions/legacy"
                element={
                  <ProtectedRoute required={["view_sellout", "manage_sellout"]}>
                    <SellOutPromotionsLegacy />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sellout/promotions/:id"
                element={
                  <ProtectedRoute required={["view_sellout", "manage_sellout"]}>
                    <SellOutPromiseDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sellout/missing"
                element={
                  <ProtectedRoute required={["view_sellout", "manage_sellout"]}>
                    <SellOutMissing />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sellout/documents"
                element={
                  <ProtectedRoute required={["view_sellout", "manage_sellout"]}>
                    <SellOutDocumentList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sellout/documents/create"
                element={
                  <ProtectedRoute required={"manage_sellout"}>
                    <SellOutDocumentCreate />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/orders/kardex/recepciones"
                element={
                  <ProtectedRoute required={"view_invoice"}>
                    <KardexRecepciones />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/orders/kardex/homogeneo"
                element={
                  <ProtectedRoute required={"view_invoice"}>
                    <KardexHomogeneo />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoice/reports/provider-status"
                element={
                  <ProtectedRoute required={"view_invoice"}>
                    <InvoiceProviderStatusReport />
                  </ProtectedRoute>
                }
              />

              {/* Admin */}
              <Route
                path="/admin/permissions"
                element={
                  <ProtectedRoute required={"view_permissions"}>
                    <PermissionCatalogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/roles"
                element={
                  <ProtectedRoute required={"manage_roles"}>
                    <RoleAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/user-assignments"
                element={
                  <ProtectedRoute required={"manage_users"}>
                    <UserCompanyAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute required={"view_users"}>
                    <UsersAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/companies"
                element={
                  <ProtectedRoute required={"company_manage_profile" /* ajusta si usas otro permiso */}>
                    <CompanyAdminPage />
                  </ProtectedRoute>
                }
              />

              <Route path="/no-access" element={<NoAccessPage />} />

              {/* Payments */}
              <Route
                path="/payment/make-payment"
                element={
                  <ProtectedRoute required={"settle_payment"}>
                    <CreatePayment />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment/receipts"
                element={
                  <ProtectedRoute required={"settle_payment"}>
                    <ReceiptList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment/credit-notes"
                element={
                  <ProtectedRoute required={"settle_payment"}>
                    <CreditNoteList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/debts/"
                element={
                  <ProtectedRoute required={"settle_payment"}>
                    <DebtList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments"
                element={
                  <ProtectedRoute>
                    <PaymentsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/abasto"
                element={
                  <ProtectedRoute required={"settle_payment"}>
                    <AbastoObligations />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments/:id"
                element={
                  <ProtectedRoute>
                    <PaymentDetailView />
                  </ProtectedRoute>
                }
              />

              {/* Perfil usuario */}
              <Route
                path="/user-profile"
                element={
                  <ProtectedRoute>
                    <UserProfile />
                  </ProtectedRoute>
                }
              />

              {/* Catálogos */}
              <Route
                path="/catalogs/warehouses"
                element={
                  <ProtectedRoute required={"view_catalogs"}>
                    <WarehouseList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/catalogs/formats"
                element={
                  <ProtectedRoute required={"view_catalogs"}>
                    <FormatList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/catalogs/categories"
                element={
                  <ProtectedRoute required={"view_catalogs"}>
                    <CategoryList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/catalogs/price-types"
                element={
                  <ProtectedRoute required={"view_catalogs"}>
                    <PriceTypeList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/catalogs/objective-types"
                element={
                  <ProtectedRoute required={"view_catalogs"}>
                    <ObjectiveTypeList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/catalogs/invoice-types"
                element={
                  <ProtectedRoute required={"view_catalogs"}>
                    <InvoiceTypeList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/catalogs/receive-notes"
                element={
                  <ProtectedRoute required={"view_catalogs"}>
                    <ReceiveNotesPage />
                  </ProtectedRoute>
                }
              />

              {/* 404 interna (si quieres usarla desde ProtectedRoute o links internos) */}
              {/* <Route path="/404" element={<NotFoundPage />} /> */}

              {/* Cualquier ruta desconocida => redirige a /home */}
              {/* <Route path="*" element={<Navigate to="/home" replace />} /> */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </Box>

        {/* Footer empresarial */}
        <AppFooter />
      </Box>
    </div>
  );
}

function AppRoot() {
  const { company } = useAuth();
  const primary = resolvePrimary(company?.colorTheme);
  const theme = useMemo(() => createTheme({
    palette: {
      primary: { main: primary },
      secondary: { main: DEFAULT_SECONDARY },
    },
  }), [primary]);

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <AppShell />
      </Router>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoot />
    </AuthProvider>
  );
}

export default App;
